/**
 * InsightsPage — Phase 7 rebuild (2026-05-30).
 *
 * Hits GET /api/insights?range=... and renders 8 sections matching the
 * locked preview at heymotek-web/src/pages/preview/dashboard/insights.astro:
 *   1. Page header
 *   2. Date-chip row (7 chips + Custom popover via Calendar mode="range")
 *   3. Fire — full-bleed card with 3 fixed timeframe cells
 *   4. By-platform — Google Ads / Meta / GA cards (GA renders "—" per Q6)
 *   5. Match trend — Recharts AreaChart via shadcn ChartContainer
 *   6. Cross-source moat — 3 tiles
 *   7. Customer list — count + View contacts link
 *   8. Top campaigns — desktop <table> + mobile compressed cards
 *
 * Backend contract: api/routes/insights.py.
 * Per Q1 lock: Fire + By-platform cells stay fixed Today/7d/30d
 * regardless of chip. Other sections respond to the chip.
 * Per Q6: GA cells null until G3 closes. Revenue renders the client's
 * currency (clients.currency via useCurrency), defaulting to USD.
 */

import { useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { keepPreviousData, useQuery } from '@tanstack/react-query';
import { format } from 'date-fns';
import type { DateRange } from 'react-day-picker';
import { Calendar as CalendarIcon, Download } from 'lucide-react';
import { Area, AreaChart, CartesianGrid, XAxis, YAxis } from 'recharts';

import { apiRequest } from '@/lib/api';
import { formatCurrency } from '@/lib/format';
import { platformLogo } from '@/lib/attributionDisplay';
import { useCurrency } from '@/lib/useAuth';
import { Calendar } from '@/components/ui/calendar';
import { PageHeader } from '@/components/PageHeader';
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '@/components/ui/popover';
import {
  ChartContainer,
  ChartTooltip,
  ChartTooltipContent,
  type ChartConfig,
} from '@/components/ui/chart';
import { SkeletonBox, SkeletonGroup } from '@/components/Skeleton';
import { ErrorState } from '@/components/ErrorState';

// ──────────────────────────────────────────────────────────────────────
// Types
// ──────────────────────────────────────────────────────────────────────

type RangePreset =
  | '24h'
  | '7d'
  | '30d'
  | 'this_month'
  | 'last_month'
  | '90d'
  | 'custom';

type CampaignPlatform = 'google_ads' | 'facebook' | 'organic' | 'other';

interface PlatformStats {
  today: number | null;
  last_7d: number | null;
  last_30d: number | null;
  last_fired_at: string | null;
}

// Fixed slice (range-independent) — Fire hero + By-platform cards.
// Hit with ?include=fire,by_platform on a long-cache query. Doesn't
// refetch when the user changes the chip (audit item 12).
interface InsightsFixed {
  period: { range: string; window_start: string; window_end: string };
  fire: { today: number; last_7d: number; last_30d: number };
  by_platform: {
    google_ads: PlatformStats;
    facebook: PlatformStats;
    google_analytics: PlatformStats;
  };
}

// Range-driven slice — Match trend + Cross-source + Customer list + Top
// campaigns. Hit with ?range=<chip>&include=<these>. Refetches on chip
// change with keepPreviousData so the cards don't flash a skeleton.
interface InsightsRange {
  period: { range: string; window_start: string; window_end: string };
  match_trend: {
    period_start: string;
    period_end: string;
    daily: { date: string; count: number }[];
  };
  cross_source: {
    from_google: number;
    from_meta: number;
    from_organic: number;
  };
  customer_list: { count: number };
  top_campaigns: {
    name: string;
    platform: CampaignPlatform;
    contacts: number;
    won: number;
    revenue: number;
  }[];
}

const RANGE_LABELS: Record<Exclude<RangePreset, 'custom'>, string> = {
  '24h': '24 hours',
  '7d': '7 days',
  '30d': '30 days',
  this_month: 'This month',
  last_month: 'Last month',
  '90d': '90 days',
};

const PRESETS: Exclude<RangePreset, 'custom'>[] = [
  '24h',
  '7d',
  '30d',
  'this_month',
  'last_month',
  '90d',
];

// Match trend line — green per brand-sheet §4 "Match" pattern (green-600).
const CHART_CONFIG = {
  count: {
    label: 'Contacts',
    color: '#16a34a',
  },
} satisfies ChartConfig;

// ──────────────────────────────────────────────────────────────────────
// Formatters
// ──────────────────────────────────────────────────────────────────────

function fmtNum(n: number | null | undefined): string {
  if (n === null || n === undefined) return '—';
  return n.toLocaleString('en-US');
}

function fmtRelative(iso: string | null): string {
  if (!iso) return '—';
  const d = new Date(iso);
  const sec = Math.floor((Date.now() - d.getTime()) / 1000);
  if (sec < 60) return 'Just now';
  if (sec < 3600) return `${Math.floor(sec / 60)}m ago`;
  if (sec < 86400) return `${Math.floor(sec / 3600)}h ago`;
  return format(d, 'd MMM');
}

function platformLabel(p: CampaignPlatform): string {
  return (
    {
      google_ads: 'Google Ads',
      facebook: 'Meta Ads',
      organic: 'Organic',
      other: 'Other',
    } as const
  )[p];
}

function platformIconSrc(p: CampaignPlatform): string | null {
  return (
    {
      google_ads: platformLogo('google-ads'),
      facebook: platformLogo('meta'),
      organic: platformLogo('google-multicolor'),
      other: null,
    } as const
  )[p];
}

// ──────────────────────────────────────────────────────────────────────
// Main component
// ──────────────────────────────────────────────────────────────────────

export function InsightsPage() {
  const [range, setRange] = useState<RangePreset>('30d');
  const [customRange, setCustomRange] = useState<DateRange | undefined>();

  const customParams = useMemo(() => {
    if (range !== 'custom' || !customRange?.from || !customRange?.to)
      return null;
    return {
      from: format(customRange.from, 'yyyy-MM-dd'),
      to: format(customRange.to, 'yyyy-MM-dd'),
    };
  }, [range, customRange]);

  const rangeQueryString = useMemo(() => {
    if (range === 'custom') {
      if (!customParams) return null;
      return `range=custom&from=${customParams.from}&to=${customParams.to}`;
    }
    return `range=${range}`;
  }, [range, customParams]);

  // Fixed slice — Fire + By-platform. Range-independent (chip changes do
  // not refetch). 5-minute stale time since fire counts trail real-time
  // by minutes anyway. Single query key so the cache survives navigation.
  const fixedQuery = useQuery({
    queryKey: ['insights', 'fixed'],
    queryFn: () =>
      apiRequest<InsightsFixed>('/insights?include=fire,by_platform'),
    staleTime: 5 * 60_000,
  });

  // Range slice — Match trend + Cross-source + Customer list + Top
  // campaigns. Refetches on chip change; keepPreviousData prevents the
  // skeleton flash between range switches.
  const rangeQuery = useQuery({
    queryKey: ['insights', 'range', { range, ...(customParams ?? {}) }],
    queryFn: () =>
      apiRequest<InsightsRange>(
        `/insights?${rangeQueryString}&include=match_trend,cross_source,customer_list,top_campaigns`,
      ),
    staleTime: 30_000,
    enabled: rangeQueryString !== null,
    placeholderData: keepPreviousData,
  });

  // Initial-mount loading: both queries empty.
  const isInitialLoading =
    (fixedQuery.isLoading && !fixedQuery.data) ||
    (rangeQuery.isLoading && !rangeQuery.data) ||
    (range === 'custom' && !customParams);

  if (isInitialLoading) {
    return <InsightsPageSkeleton />;
  }

  const error = fixedQuery.error ?? rangeQuery.error;
  if (error) {
    return (
      <div className="px-4 py-6 sm:px-6 lg:px-8 lg:py-10">
        <ErrorState
          title="Couldn't load insights"
          description="Please retry. If the problem persists, refresh the page."
          onRetry={() => {
            fixedQuery.refetch();
            rangeQuery.refetch();
          }}
        />
      </div>
    );
  }

  const fixed = fixedQuery.data;
  const rangeData = rangeQuery.data;
  if (!fixed || !rangeData) return null;

  return (
    <div className="space-y-6 px-4 py-6 sm:px-6 lg:px-8 lg:py-10">
      <PageHeader title="Insights" subtitle="What HeyMotek delivered for you." />

      {/* Fire (fixed Today/7d/30d per Q1 lock — range-independent) */}
      <FireSection fire={fixed.fire} />

      {/* By-platform (range-independent) */}
      <ByPlatformSection byPlatform={fixed.by_platform} />

      {/* Date chip row — controls only the range-dependent sections below.
          Moved here from the page top so it sits visually adjacent to
          the data it affects (audit follow-on item 3). Fire + By-platform
          above stay fixed Today/7d/30d regardless of chip.
          Border-top + section label gives the chip row clear distinction
          from the By-platform cards above — otherwise the chips look like
          a control strip nested inside the previous section. Same chrome
          as the other section heads on this page. */}
      <div className="border-t border-dark/10 pt-6">
        <h2 className="mb-3 px-1 text-base font-bold uppercase tracking-wider text-muted-foreground">
          Range
        </h2>
        <DateChipRow
          range={range}
          customRange={customRange}
          onRangeChange={(r) => {
            setRange(r);
            if (r !== 'custom') setCustomRange(undefined);
          }}
          onCustomRangeChange={(r) => {
            setRange('custom');
            setCustomRange(r);
          }}
        />
      </div>

      {/* Match trend (range-dependent) */}
      <MatchSection
        total={rangeData.customer_list.count}
        daily={rangeData.match_trend.daily}
      />

      {/* Cross-source moat (range-dependent) */}
      <CrossSourceSection crossSource={rangeData.cross_source} />

      {/* Customer list (range-dependent count) */}
      <CustomerListSection count={rangeData.customer_list.count} />

      {/* Top campaigns (range-dependent) */}
      <TopCampaignsSection campaigns={rangeData.top_campaigns} />
    </div>
  );
}

// ──────────────────────────────────────────────────────────────────────
// Sub-sections
// ──────────────────────────────────────────────────────────────────────

function DateChipRow({
  range,
  customRange,
  onRangeChange,
  onCustomRangeChange,
}: {
  range: RangePreset;
  customRange: DateRange | undefined;
  onRangeChange: (r: RangePreset) => void;
  onCustomRangeChange: (r: DateRange | undefined) => void;
}) {
  const customLabel = customRange?.from
    ? customRange.to
      ? `${format(customRange.from, 'd MMM')} – ${format(customRange.to, 'd MMM')}`
      : format(customRange.from, 'd MMM')
    : 'Custom';

  return (
    <div className="flex flex-wrap gap-2">
      {PRESETS.map((p) => {
        const active = range === p;
        return (
          <button
            key={p}
            type="button"
            onClick={() => onRangeChange(p)}
            className={
              active
                ? 'inline-flex items-center rounded-full border border-info/30 bg-info/10 px-3 py-1.5 text-sm font-semibold text-info'
                : 'inline-flex items-center rounded-full border border-dark/10 bg-white px-3 py-1.5 text-sm font-semibold text-dark transition-colors hover:border-dark/30'
            }
          >
            {RANGE_LABELS[p]}
          </button>
        );
      })}
      <Popover>
        <PopoverTrigger asChild>
          <button
            type="button"
            className={
              range === 'custom'
                ? 'inline-flex items-center gap-1.5 rounded-full border border-info/30 bg-info/10 px-3 py-1.5 text-sm font-semibold text-info'
                : 'inline-flex items-center gap-1.5 rounded-full border border-dark/10 bg-white px-3 py-1.5 text-sm font-semibold text-dark transition-colors hover:border-dark/30'
            }
          >
            <CalendarIcon className="h-3.5 w-3.5" />
            {customLabel}
          </button>
        </PopoverTrigger>
        <PopoverContent className="w-auto p-0" align="start">
          <Calendar
            mode="range"
            selected={customRange}
            onSelect={onCustomRangeChange}
            numberOfMonths={2}
          />
        </PopoverContent>
      </Popover>
    </div>
  );
}

function FireSection({
  fire,
}: {
  fire: { today: number; last_7d: number; last_30d: number };
}) {
  return (
    <section className="rounded-3xl border border-dark/5 bg-card p-6 shadow-card sm:p-8 lg:p-10">
      <p className="text-xs font-bold uppercase tracking-wider text-muted-foreground">
        Fire
      </p>
      <p className="mt-1 text-2xl font-extrabold text-dark sm:text-3xl">
        Conversions fired back to your ad platforms automatically
      </p>
      <div className="mt-6 grid grid-cols-3 gap-4 sm:gap-8">
        <FireCell label="Today" value={fire.today} />
        <FireCell label="Last 7 days" value={fire.last_7d} />
        <FireCell label="Last 30 days" value={fire.last_30d} />
      </div>
    </section>
  );
}

function FireCell({ label, value }: { label: string; value: number }) {
  return (
    <div>
      <p className="text-xs font-bold uppercase tracking-wider text-muted-foreground">
        {label}
      </p>
      <p className="mt-1 text-4xl font-extrabold leading-none tabular-nums text-dark sm:text-5xl lg:text-6xl">
        {fmtNum(value)}
      </p>
      <p className="mt-1 text-base text-muted-foreground">fired</p>
    </div>
  );
}

function ByPlatformSection({
  byPlatform,
}: {
  byPlatform: InsightsFixed['by_platform'];
}) {
  return (
    <div>
      <h2 className="mb-3 px-1 text-base font-bold uppercase tracking-wider text-muted-foreground">
        By platform
      </h2>
      <div className="grid grid-cols-1 gap-4 md:grid-cols-3">
        <PlatformCard
          title="Google Ads"
          iconSrc={platformLogo('google-ads')}
          stats={byPlatform.google_ads}
        />
        <PlatformCard
          title="Meta Ads"
          iconSrc={platformLogo('meta')}
          stats={byPlatform.facebook}
        />
        <PlatformCard
          title="Google Analytics"
          iconSrc={platformLogo('google-analytics')}
          stats={byPlatform.google_analytics}
        />
      </div>
    </div>
  );
}

function PlatformCard({
  title,
  iconSrc,
  stats,
}: {
  title: string;
  iconSrc: string;
  stats: PlatformStats;
}) {
  const isLive = stats.last_fired_at !== null;
  return (
    <div className="rounded-3xl border border-green-200 bg-card p-5 shadow-card sm:p-6">
      <div className="mb-4 flex items-center gap-3">
        <img src={iconSrc} alt="" className="h-8 w-8 shrink-0" loading="lazy" />
        <div className="min-w-0 flex-1">
          <h3 className="text-lg font-extrabold text-dark">{title}</h3>
          {isLive && (
            <span className="rounded-full bg-green-100 px-2 py-0.5 text-xs font-bold uppercase tracking-wider text-green-700">
              Updated
            </span>
          )}
        </div>
      </div>
      <div className="space-y-2 text-base">
        <PlatformRow label="Today" value={stats.today} />
        <PlatformRow label="Last 7 days" value={stats.last_7d} />
        <PlatformRow label="Last 30 days" value={stats.last_30d} />
        <div className="flex justify-between border-t border-dark/5 pt-2">
          <span className="text-muted-foreground">Last fire</span>
          <span className="text-dark">{fmtRelative(stats.last_fired_at)}</span>
        </div>
      </div>
    </div>
  );
}

function PlatformRow({
  label,
  value,
}: {
  label: string;
  value: number | null;
}) {
  return (
    <div className="flex justify-between">
      <span className="text-muted-foreground">{label}</span>
      <span className="font-bold tabular-nums text-dark">{fmtNum(value)}</span>
    </div>
  );
}

function MatchSection({
  total,
  daily,
}: {
  total: number;
  daily: { date: string; count: number }[];
}) {
  if (daily.length === 0) {
    return (
      <section className="rounded-3xl border border-dark/5 bg-card p-6 shadow-card sm:p-8">
        <p className="text-xs font-bold uppercase tracking-wider text-muted-foreground">
          Match
        </p>
        <p className="mt-2 text-base text-muted-foreground">
          No contacts in this range yet.
        </p>
      </section>
    );
  }
  return (
    <section className="rounded-3xl border border-dark/5 bg-card p-6 shadow-card sm:p-8">
      <div className="mb-4">
        <p className="text-xs font-bold uppercase tracking-wider text-muted-foreground">
          Match
        </p>
        <p className="mt-1 text-3xl font-extrabold text-dark sm:text-4xl">
          <span className="tabular-nums">{fmtNum(total)}</span> contacts matched
          to the ads that brought them
        </p>
        <p className="mt-2 text-base text-muted-foreground">In this range</p>
      </div>
      <ChartContainer config={CHART_CONFIG} className="h-[220px] w-full">
        <AreaChart
          data={daily}
          margin={{ top: 10, right: 10, bottom: 0, left: 0 }}
        >
          <CartesianGrid
            vertical={false}
            strokeDasharray="2 2"
            stroke="rgb(15 23 42 / 0.05)"
          />
          <XAxis
            dataKey="date"
            tickLine={false}
            axisLine={false}
            tickMargin={8}
            tickFormatter={(v: string) => format(new Date(v), 'd MMM')}
            interval="preserveStartEnd"
          />
          <YAxis hide />
          <ChartTooltip content={<ChartTooltipContent indicator="line" />} />
          <Area
            type="monotone"
            dataKey="count"
            stroke="var(--color-count)"
            strokeWidth={2.5}
            fill="var(--color-count)"
            fillOpacity={0.15}
          />
        </AreaChart>
      </ChartContainer>
    </section>
  );
}

function CrossSourceSection({
  crossSource,
}: {
  crossSource: {
    from_google: number;
    from_meta: number;
    from_organic: number;
  };
}) {
  return (
    <section className="rounded-3xl border border-dark/5 bg-card p-6 shadow-card sm:p-8">
      <p className="text-xs font-bold uppercase tracking-wider text-muted-foreground">
        Cross-source
      </p>
      <p className="mt-1 text-2xl font-extrabold text-dark sm:text-3xl">
        Every contact trained every ad platform
      </p>
      <p className="mb-5 mt-2 max-w-prose text-base text-muted-foreground">
        Contacts from Google fed Meta. Contacts from Meta fed Google. Organic
        contacts fed both. The algorithms get smarter from every customer,
        regardless of where they came from.
      </p>
      <div className="grid grid-cols-3 gap-3 sm:gap-6">
        <CrossTile
          iconSrc={platformLogo('google-ads')}
          label="From Google"
          value={crossSource.from_google}
        />
        <CrossTile
          iconSrc={platformLogo('meta')}
          label="From Meta"
          value={crossSource.from_meta}
        />
        <CrossTile
          iconSrc={platformLogo('google-multicolor')}
          label="From Organic"
          value={crossSource.from_organic}
        />
      </div>
    </section>
  );
}

function CrossTile({
  iconSrc,
  label,
  value,
}: {
  iconSrc: string;
  label: string;
  value: number;
}) {
  return (
    <div className="text-center">
      <div className="mb-2 flex items-center justify-center gap-1.5">
        <img src={iconSrc} alt="" className="h-5 w-5 shrink-0" loading="lazy" />
        <p className="text-xs font-bold uppercase tracking-wider text-muted-foreground">
          {label}
        </p>
      </div>
      <p className="text-3xl font-extrabold tabular-nums text-dark sm:text-4xl">
        {fmtNum(value)}
      </p>
      <p className="mt-1 text-sm text-muted-foreground">trained both</p>
    </div>
  );
}

function CustomerListSection({ count }: { count: number }) {
  return (
    <section className="rounded-3xl border border-dark/5 bg-card p-6 shadow-card sm:p-8">
      <div className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
        <div>
          <p className="text-xs font-bold uppercase tracking-wider text-muted-foreground">
            Customer list
          </p>
          <p className="mt-1 text-2xl font-extrabold text-dark sm:text-3xl">
            <span className="tabular-nums">{fmtNum(count)}</span> phone numbers,
            attribution intact
          </p>
          <p className="mt-2 text-base text-muted-foreground">
            Yours to keep. Yours to export.
          </p>
        </div>
        <Link
          to="/contacts"
          className="inline-flex shrink-0 items-center justify-center gap-2 rounded-lg border border-dark/15 bg-white px-4 py-2 text-sm font-semibold text-dark transition-colors hover:border-dark/30"
        >
          <Download className="h-4 w-4" />
          View contacts
        </Link>
      </div>
    </section>
  );
}

function TopCampaignsSection({
  campaigns,
}: {
  campaigns: InsightsRange['top_campaigns'];
}) {
  const currency = useCurrency();
  if (campaigns.length === 0) {
    return (
      <div>
        <h2 className="mb-3 px-1 text-base font-bold uppercase tracking-wider text-muted-foreground">
          Top campaigns
        </h2>
        <div className="rounded-3xl border border-dark/5 bg-card p-8 text-center text-sm text-muted-foreground shadow-card">
          No campaign-attributed contacts in this range.
        </div>
      </div>
    );
  }

  return (
    <div>
      <h2 className="mb-3 px-1 text-base font-bold uppercase tracking-wider text-muted-foreground">
        Top campaigns
      </h2>
      <div className="overflow-hidden rounded-3xl border border-dark/5 bg-card shadow-card">
        {/* Desktop table — X2 header per brand sheet §4 */}
        <div className="hidden md:block">
          <table className="w-full border-collapse">
            <thead>
              <tr className="border-b-2 border-dark bg-white text-left text-sm font-bold uppercase tracking-widest text-dark">
                <th className="px-6 py-4 font-bold">Campaign</th>
                <th className="px-6 py-4 font-bold">Platform</th>
                <th className="px-6 py-4 text-right font-bold">Contacts</th>
                <th className="px-6 py-4 text-right font-bold">Won</th>
                <th className="px-6 py-4 text-right font-bold">Revenue</th>
              </tr>
            </thead>
            <tbody>
              {campaigns.map((c, i) => {
                const iconSrc = platformIconSrc(c.platform);
                return (
                  <tr
                    key={c.name}
                    className={`${i > 0 ? 'border-t-2 border-dark/20' : ''} transition-colors hover:bg-bg-tint`}
                  >
                    <td className="px-6 py-4 align-middle text-base font-semibold text-dark">
                      {c.name}
                    </td>
                    <td className="px-6 py-4 align-middle text-sm text-muted-foreground">
                      <span className="inline-flex items-center gap-2 whitespace-nowrap">
                        {iconSrc && (
                          <img
                            src={iconSrc}
                            alt=""
                            className="h-3.5 w-3.5 shrink-0"
                            loading="lazy"
                          />
                        )}
                        {platformLabel(c.platform)}
                      </span>
                    </td>
                    <td className="px-6 py-4 align-middle text-right text-base font-semibold tabular-nums text-dark">
                      {fmtNum(c.contacts)}
                    </td>
                    <td className="px-6 py-4 align-middle text-right text-base tabular-nums text-dark">
                      {fmtNum(c.won)}
                    </td>
                    <td className="px-6 py-4 align-middle text-right text-base font-semibold tabular-nums text-dark">
                      {formatCurrency(c.revenue, currency)}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
        {/* Mobile compressed cards */}
        <div className="md:hidden">
          {campaigns.map((c, i) => {
            const iconSrc = platformIconSrc(c.platform);
            return (
              <div
                key={c.name}
                className={`px-5 py-4 ${i > 0 ? 'border-t border-dark/5' : ''}`}
              >
                <div className="mb-2 flex items-start justify-between gap-3">
                  <span className="min-w-0 truncate text-sm font-semibold text-dark">
                    {c.name}
                  </span>
                  <span className="shrink-0 text-sm font-bold tabular-nums text-dark">
                    {formatCurrency(c.revenue, currency)}
                  </span>
                </div>
                <div className="mb-2 flex items-center gap-2 text-xs text-muted-foreground">
                  {iconSrc && (
                    <img
                      src={iconSrc}
                      alt=""
                      className="h-3.5 w-3.5 shrink-0"
                      loading="lazy"
                    />
                  )}
                  {platformLabel(c.platform)}
                </div>
                <div className="text-xs text-muted-foreground">
                  {fmtNum(c.contacts)} contacts · {fmtNum(c.won)} won
                </div>
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
}

// ──────────────────────────────────────────────────────────────────────
// Skeleton
// ──────────────────────────────────────────────────────────────────────

function InsightsPageSkeleton() {
  return (
    <div className="px-4 py-6 sm:px-6 lg:px-8 lg:py-10">
      <SkeletonGroup className="space-y-6">
        <div>
          <SkeletonBox tone="prominent" className="h-10 w-40 sm:h-12" />
          <SkeletonBox className="mt-3 h-4 w-72 max-w-full" />
        </div>
        <SkeletonBox className="h-10 w-full max-w-2xl rounded-full" />
        <SkeletonBox className="h-44 w-full rounded-3xl" />
        <div className="grid grid-cols-1 gap-4 md:grid-cols-3">
          <SkeletonBox className="h-48 w-full rounded-3xl" />
          <SkeletonBox className="h-48 w-full rounded-3xl" />
          <SkeletonBox className="h-48 w-full rounded-3xl" />
        </div>
        <SkeletonBox className="h-72 w-full rounded-3xl" />
        <SkeletonBox className="h-56 w-full rounded-3xl" />
        <SkeletonBox className="h-32 w-full rounded-3xl" />
        <SkeletonBox className="h-64 w-full rounded-3xl" />
      </SkeletonGroup>
    </div>
  );
}
